import { supabase } from "@/integrations/supabase/client";
import {
  AiPromptTemplate,
  AiPromptTemplateFormData,
} from "@/types/aiPromptTemplate";

// Transform DB row to app type
const transformTemplate = (row: any): AiPromptTemplate => ({
  id: row.id,
  purpose: row.purpose,
  name: row.name,
  content: row.content,
  description: row.description,
  isActive: row.is_active,
  version: row.version,
  metadata: row.metadata || {},
  personaId: row.persona_id,
  personaName: row.ai_personas?.name ?? undefined,
  personaSlug: row.ai_personas?.slug ?? undefined,
  createdBy: row.created_by,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

// ------- CRUD -------

export const fetchAiPromptTemplates = async (): Promise<AiPromptTemplate[]> => {
  const { data, error } = await supabase
    .from("ai_prompt_templates" as any)
    .select("*, ai_personas(name, slug)")
    .order("purpose")
    .order("version", { ascending: false });

  if (error) throw error;
  return (data || []).map(transformTemplate);
};

export const fetchAiPromptTemplate = async (
  id: number
): Promise<AiPromptTemplate | null> => {
  const { data, error } = await supabase
    .from("ai_prompt_templates" as any)
    .select("*, ai_personas(name, slug)")
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null;
    throw error;
  }
  return data ? transformTemplate(data) : null;
};

export const createAiPromptTemplate = async (
  formData: AiPromptTemplateFormData
): Promise<AiPromptTemplate> => {
  // Calculate next version for this (purpose, persona_id) combination
  let query = supabase
    .from("ai_prompt_templates" as any)
    .select("version")
    .eq("purpose", formData.purpose)
    .order("version", { ascending: false })
    .limit(1);

  if (formData.personaId) {
    query = query.eq("persona_id", formData.personaId);
  } else {
    query = query.is("persona_id", null);
  }

  const { data: existing } = await query;
  const nextVersion = existing && existing.length > 0 ? existing[0].version + 1 : 1;

  const { data, error } = await supabase
    .from("ai_prompt_templates" as any)
    .insert({
      purpose: formData.purpose,
      name: formData.name,
      content: formData.content,
      description: formData.description || null,
      metadata: formData.metadata || {},
      persona_id: formData.personaId || null,
      version: nextVersion,
      is_active: false,
    })
    .select("*, ai_personas(name, slug)")
    .single();

  if (error) throw error;
  if (!data) throw new Error("No data returned after insert");
  return transformTemplate(data);
};

export const updateAiPromptTemplate = async (
  id: number,
  formData: Partial<AiPromptTemplateFormData>
): Promise<AiPromptTemplate> => {
  const updateData: Record<string, unknown> = {};
  if (formData.name !== undefined) updateData.name = formData.name;
  if (formData.content !== undefined) updateData.content = formData.content;
  if (formData.description !== undefined)
    updateData.description = formData.description || null;
  if (formData.metadata !== undefined) updateData.metadata = formData.metadata;
  if (formData.personaId !== undefined)
    updateData.persona_id = formData.personaId || null;
  // purpose is intentionally not updatable after creation

  const { data, error } = await supabase
    .from("ai_prompt_templates" as any)
    .update(updateData)
    .eq("id", id)
    .select("*, ai_personas(name, slug)")
    .single();

  if (error) throw error;
  if (!data) throw new Error("No data returned after update");
  return transformTemplate(data);
};

export const activateAiPromptTemplate = async (id: number): Promise<void> => {
  const { error } = await supabase.rpc("activate_ai_prompt_template", {
    template_id: id,
  });
  if (error) throw error;
};

export const duplicateAiPromptTemplate = async (
  id: number
): Promise<AiPromptTemplate> => {
  const source = await fetchAiPromptTemplate(id);
  if (!source) throw new Error("Template not found");

  return createAiPromptTemplate({
    purpose: source.purpose,
    name: `${source.name} (copy)`,
    content: source.content,
    description: source.description || undefined,
    metadata: source.metadata,
    personaId: source.personaId,
  });
};

export const deleteAiPromptTemplate = async (id: number): Promise<void> => {
  // Safety check: don't delete active templates
  const template = await fetchAiPromptTemplate(id);
  if (template?.isActive) {
    throw new Error("Cannot delete an active template. Activate another template first.");
  }

  const { error } = await supabase
    .from("ai_prompt_templates" as any)
    .delete()
    .eq("id", id);

  if (error) throw error;
};
